import { Box, Button, Checkbox, Grid, ListItem, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AxiosResponse } from 'axios';
import Breadcrumbs from 'components/breadcrumbs';
import Dialog from 'components/dialog';
import FilterSidebar from 'components/filter-sidebar';
import FlexBox from 'components/flex-box';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';
import Pagination from 'components/pagination';

import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TextField from 'components/text-field';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { CheckCircle, Search, Square, SquareCheckBig } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import _, { isEmpty } from 'underscore';
import { CreateManualFeedInput } from 'views/feed-management/features/manual-feeds/types/create-manual-feed-input.type';
import { ManualFeedListItem } from 'views/feed-management/features/manual-feeds/types/manual-feed-list-item.type';
import { ManualFeed } from 'views/feed-management/features/manual-feeds/types/manual-feed.type';
import { UpdateManualFeedsStatusInput } from 'views/feed-management/features/manual-feeds/types/update-manual-feeds-status.type';
import FeedManagementTabsHeader from '../../components/feed-management-tabs-header';

import CheckboxSelectItem from 'components/checkbox-select-item';
import ClearSelection from 'components/clear-selection';
import Paper from 'components/paper';
import Switch from 'components/switch';
import { usePagination } from 'hooks/use-pagination';
import { FixedSizeList } from 'react-window';
import { getFeedManagementCustomers } from 'views/feed-management/features/customers/api/get-customers';
import { putCustomerLinkFeeds } from 'views/feed-management/features/customers/api/update-customers';
import { UpdateCustomerManualFeedsLinkInput } from 'views/feed-management/features/customers/types/update-customer-manual-feeds-link-input.type';
import { postManualFeed } from './api/create-manual-feed';
import { getManualFeedsList } from './api/get-manual-feeds';
import { putManualFeedsStatus } from './api/update-manual-feeds-status';

type FormValues = {
  feedName: string;
  pelletSize: string;
};

type ListManualFeedsProps = {};

const ListManualFeeds: React.FC<ListManualFeedsProps> = () => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const intl = useIntl();
  const { account } = useAccountState();
  const snackbarDispatch = useSnackbarDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const PAGE_NAME_ID = 'feedManagement.listManualFeeds';
  const PAGINATION_LIMIT = 10;

  // STATE
  const [feeds, setFeeds] = useState<ManualFeedListItem[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [active, setActive] = useState<boolean>(true);
  const [feedFilter, setFeedFilter] = useState<string>('All');

  const [selectAllFeeds, setSelectAllFeeds] = useState<Record<number, boolean>>({});
  const [selectedFeeds, setSelectedFeeds] = useState<string[]>([]);
  const manageFeedsDialogRef = useRef<React.ElementRef<typeof Dialog>>();
  const customerLinkDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const [customerSearch, setCustomerSearch] = useState<string>('');
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const { currentPage, from, to, handlePagination } = usePagination(PAGINATION_LIMIT);

  // FORMS
  const { handleSubmit, control } = useForm<FormValues>({
    defaultValues: {
      feedName: '',
      pelletSize: '',
    },
    shouldUnregister: true,
  });

  // FETCH DATA

  const { data: customers } = useGet(['getFeedManagementCustomers'], () => getFeedManagementCustomers());

  const { data: feedData, isFetching } = useGet(
    ['getManualFeedsList', account.company!.companyId],
    (context) => getManualFeedsList(String(context.queryKey[1])),
    {
      enabled: !!account.company?.companyId,
      select: (data) =>
        _.sortBy(
          data.filter((feed) => {
            switch (feedFilter) {
              case 'All':
                return true;
              case 'CurrentCompanyAndMe':
                return (
                  feed.assignedCompanies.some((ac) => ac.id === account.company?.companyId) &&
                  feed.assignedUsers.some((ac) => ac.id === account.currentUser?.id)
                );
              case 'CurrentCompany':
                return feed.assignedCompanies.some((ac) => ac.id === account.company?.companyId);
              case 'Me':
                return feed.assignedUsers.some((ac) => ac.id === account.currentUser?.id);
              default:
                return true;
            }
          }),
          (feed) => feed.name.toLowerCase()
        ),
    }
  );

  useEffect(() => {
    setFeeds(
      _.chain(feedData)
        .sortBy((feed) => feed.name.toLowerCase())
        .value()
    );
  }, [feedData]);

  // POST DATA
  const createFeed = usePost<ManualFeed, CreateManualFeedInput>(
    (request) => postManualFeed(account.company!.companyId, request),
    (response) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      history.push(`/feed-management/manual-feeds/${response.id}`);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const updateManualFeedsStatus = usePost<AxiosResponse, { globalCompanyId: string; feeds: UpdateManualFeedsStatusInput }>(
    (request) => putManualFeedsStatus(request.globalCompanyId, request.feeds),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      manageFeedsDialogRef.current?.setOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['getManualFeedsList']
      });
      resetStates();
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const updateLinkCustomerFeed = usePost<AxiosResponse, { globalCompanyId: string; data: UpdateCustomerManualFeedsLinkInput }>(
    (request) => putCustomerLinkFeeds(request.globalCompanyId, request.data),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      customerLinkDialogRef.current?.setOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['getFeedManagementBestMixFeeds']
      });
      resetStates();
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const customerList = useMemo(() => new Map(customers?.map((item) => [item.id, item])), [customers]);

  const customerIds = useMemo(() => {
    return Array.from(customerList.keys());
  }, [customerList]);

  const customer = useMemo(() => Array.from(customerList, ([key, value]) => value), [customerList]);

  const customerMap = customer
    .filter((s) => s.name.toLowerCase().includes(searchValue.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  // HANDLERS

  const handleCheckAllCustomersChange = () => {
    setSelectedCustomerIds((prev) => (prev.length === customer?.length ? [] : customerIds));
  };

  const handleCustomerItemClick = (index: number) => {
    const customerId = customerMap[index].id;
    const isSelected = selectedCustomerIds.includes(customerId);
    if (isSelected) {
      setSelectedCustomerIds(selectedCustomerIds.filter((id) => id !== customerId));
    } else {
      setSelectedCustomerIds([...selectedCustomerIds, customerId]);
    }
  };

  const handleCustomerLinkFeeds = () => {
    const request: UpdateCustomerManualFeedsLinkInput = {
      companies: selectedCustomerIds,
      feeds: selectedFeeds,
    };

    updateLinkCustomerFeed.mutate({ globalCompanyId: account.company!.companyId, data: request });
  };

  const resetStates = () => {
    setSelectAllFeeds({});
    setSelectedFeeds([]);
    setSelectedCustomerIds([]);
  };

  const handleActive = () => {
    handlePagination(1);
    setActive((prev) => !prev);
  };

  const handleCreateFeed = (data: FormValues) => {
    createFeed.mutate({
      name: `${data.feedName} ${data.pelletSize}`,
    });
  };

  const handleOnClick = (url: string) => {
    history.push(url);
  };

  const handleClearSelectedFeeds = () => {
    setSelectedFeeds([]);
    setSelectAllFeeds({});
  };

  const manageFeedsHandler = () => {
    const manageFeedsData: UpdateManualFeedsStatusInput = {
      feeds: selectedFeeds,
      action: active ? 'inactivate' : 'activate',
    };
    updateManualFeedsStatus.mutate({ globalCompanyId: account.company!.companyId, feeds: manageFeedsData });
  };

  const handleSelectAllFeeds = (pageNumber: number, checked: boolean) => {
    const feedsOnPage = renderRows.slice(from, to);
    const allFeedsIdsOnPage = feedsOnPage.map((product) => product.id);
    const updatedSelectedFeeds = checked
      ? [...selectedFeeds, ...allFeedsIdsOnPage]
      : selectedFeeds.filter((id) => !allFeedsIdsOnPage.includes(id));
    setSelectedFeeds(updatedSelectedFeeds);
    setSelectAllFeeds((prevSelectAllFeeds) => ({ ...prevSelectAllFeeds, [pageNumber]: checked }));
  };

  const handleSelectFeeds = (value: string, checked: boolean) => {
    setSelectedFeeds(checked ? selectedFeeds.concat([value]) : selectedFeeds.filter((s) => s !== value));
  };

  // RENDERS
  const renderRows = useMemo(() => {
    const rows = _.chain(feeds)
      .filter((item) => item.active === active)
      .filter((item) => (isEmpty(searchValue) ? true : item.name.toLowerCase().includes(searchValue.toLowerCase())))
      .value();

    return rows;
  }, [feeds, active, searchValue]);

  useEffect(() => {
    handlePagination(1);
  }, [renderRows]);

  const renderSelectedFeeds = (selectedFeeds: string[]) => {
    const filteredProducts = feeds?.filter((u) => selectedFeeds.includes(u.id));
    return (
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} wrap="wrap" overflow={'auto'} maxHeight={'300px'}>
        {filteredProducts.map((product) => (
          <Grid size={6} key={`${product.id}`}>
            <ListItem sx={{ py: 1, px: 2, borderBottom: '1px solid #ddd' }}>
              <Typography variant="body1">{product?.name}</Typography>
            </ListItem>
          </Grid>
        ))}
      </Grid>
    );
  };

  const filterOptions = ['All', 'CurrentCompany', 'CurrentCompanyAndMe', 'Me'];

  return (
    <>
      <FeedManagementTabsHeader
        intro={
          <Typography color="white">
            <FormattedMessage id="general.confidential" />
          </Typography>
        }
        utils={<Breadcrumbs />}
      />

      <Paper transparent>
        <Grid size={12}>
          <FilterSidebar
            clearFilters={() => {
              setSearchValue('');
              setFeedFilter('All');
              setActive(true);
            }}
            showClearFilter={setFeedFilter.length !== setFeedFilter.length || !active}>
            {!!renderRows.length && active && (
              <Dialog
                maxWidth="md"
                onClose={() => {
                  setCustomerSearch('');
                  setSelectedCustomerIds([]);
                }}
                ref={customerLinkDialogRef}
                actions={
                  <Button
                    type="submit"
                    onClick={handleCustomerLinkFeeds}
                    disabled={updateLinkCustomerFeed.isPending || selectedCustomerIds.length === 0}>
                    <FormattedMessage id={`general.save`} />
                  </Button>
                }
                title={intl.formatMessage({ id: `${PAGE_NAME_ID}.linkToCustomer` })}
                button={
                  <Button
                    fullWidth={isMobile}
                    disabled={!selectAllFeeds[currentPage] && selectedFeeds.length === 0}
                    size={isMobile ? 'small' : 'medium'}>
                    <FormattedMessage id={`${PAGE_NAME_ID}.linkToCustomer`} />
                  </Button>
                }>
                <Box display={'flex'} flexDirection={'column'} gap={4}>
                  <Select
                    multiple
                    dark
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.customerSelect` })}
                    value={selectedCustomerIds}
                    MenuProps={{
                      autoFocus: false,
                    }}
                    onChange={(e) => setSelectedCustomerIds(e.target.value as string[])}
                    search={{
                      value: customerSearch,
                      onChange: setCustomerSearch,
                    }}
                    renderValue={() => {
                      const selectedCustomers = selectedCustomerIds.map((id) => {
                        return customerMap.find((customer) => customer.id === id)?.name ?? '';
                      });
                      return selectedCustomers.join(', ');
                    }}
                    checkAll={{
                      value: selectedCustomerIds.length === customerIds.length,
                      onChange: handleCheckAllCustomersChange,
                    }}>
                    <FixedSizeList
                      height={500}
                      width={500}
                      itemSize={45}
                      itemCount={
                        customerMap.filter((customer) => {
                          return customer.name.toLowerCase().includes(customerSearch.toLowerCase());
                        }).length
                      }>
                      {({ index, style }) => {
                        const filteredCustomerMap = customerMap.filter((customer) => {
                          return customer.name.toLowerCase().includes(customerSearch.toLowerCase());
                        });
                        const customer = filteredCustomerMap[index];
                        const actualIndex = customerMap.findIndex((c) => c.id === customer.id);

                        return (
                          <div key={index} style={style} onClick={() => handleCustomerItemClick(actualIndex)}>
                            <CheckboxSelectItem key={index} checked={selectedCustomerIds.includes(customer.id)} value={customer.id}>
                              {customer.name}
                            </CheckboxSelectItem>
                          </div>
                        );
                      }}
                    </FixedSizeList>
                  </Select>
                  <Typography>
                    <FormattedMessage id={`${PAGE_NAME_ID}.dialog.linkToSelectedCustomers`} />
                  </Typography>
                  <Box>{renderSelectedFeeds(selectedFeeds)}</Box>
                </Box>
              </Dialog>
            )}

            {!!renderRows.length && (
              <Dialog
                maxWidth="md"
                ref={(el) => {
                  if (el) manageFeedsDialogRef.current = el;
                }}
                actions={
                  <Button type="submit" size="small" onClick={manageFeedsHandler}>
                    <FormattedMessage id={`${PAGE_NAME_ID}.dialog.${active ? 'deactivate' : 'activate'}`} />
                  </Button>
                }
                title={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.${active ? 'deactivateTitle' : 'activateTitle'}` })}
                button={
                  <Button
                    fullWidth={isMobile}
                    disabled={!selectAllFeeds[currentPage] && selectedFeeds.length === 0}
                    size={isMobile ? 'small' : 'medium'}>
                    <FormattedMessage id={`${PAGE_NAME_ID}.${active ? 'deactivateFeeds' : 'activateFeeds'}`} />
                  </Button>
                }>
                <Box>
                  <Typography pb={5} pt={5}>
                    <FormattedMessage id={`${PAGE_NAME_ID}.dialog.${active ? 'deactivateDialog' : 'activateDialog'}`} />
                  </Typography>
                  <Box>{renderSelectedFeeds(selectedFeeds)}</Box>
                </Box>
              </Dialog>
            )}

            <Select
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.list.feedFilterLabel` })}
              value={feedFilter}
              onChange={(event) => setFeedFilter(event.target.value as string)}>
              {filterOptions.map((value) => (
                <RadioSelectItem key={value} value={value}>
                  {intl.formatMessage({ id: `${PAGE_NAME_ID}.list.feedFilterOptions.${value.charAt(0).toLowerCase() + value.slice(1)}` })}
                </RadioSelectItem>
              ))}
            </Select>
            <TextField
              label={intl.formatMessage({ id: 'general.search' })}
              value={searchValue}
              endAdornment={<Search size={18} />}
              onChange={(event) => {
                setSearchValue(event.target.value);
              }}
            />
            <Dialog
              title={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.title` })}
              button={
                <Button>
                  <FormattedMessage id={`${PAGE_NAME_ID}.createFeed`} values={{ type: '' }} />
                </Button>
              }>
              <form onSubmit={handleSubmit(handleCreateFeed)}>
                <FlexBox>
                  {createFeed.isPending && <LoadingSpinner overlay />}
                  <TextField
                    dark
                    fullWidth
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.nameLabel` })}
                    controller={{
                      name: 'feedName',
                      control: control,
                      rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                    }}
                  />
                  <TextField
                    fullWidth
                    dark
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.pelletSizeLabel` })}
                    controller={{
                      name: 'pelletSize',
                      control: control,
                      rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                    }}
                  />

                  <Box display={'flex'} justifyContent={'end'} gap={4}>
                    <Button type="submit" size="medium">
                      <FormattedMessage id={`general.create`} values={{ type: '' }} />
                    </Button>
                  </Box>
                </FlexBox>
              </form>
            </Dialog>
            <Box display={'flex'} justifyContent={'flex-end'} width={'100%'}>
              <Switch
                label={
                  active
                    ? intl.formatMessage({ id: `${PAGE_NAME_ID}.list.activeLabel` })
                    : intl.formatMessage({ id: `${PAGE_NAME_ID}.list.inactiveLabel` })
                }
                value={active}
                onChange={() => handleActive()}
              />
            </Box>
          </FilterSidebar>

          <Box display={'flex'} paddingBottom={8} alignItems={'center'}>
            {!!renderRows.length && (
              <Switch
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.selectAllFeeds` })}
                value={selectAllFeeds[currentPage] || false}
                onChange={(event) => handleSelectAllFeeds(currentPage, event.target.checked)}
              />
            )}
            {!!selectedFeeds.length && <ClearSelection onClick={handleClearSelectedFeeds} />}
          </Box>
          <List
            options={[
              { id: 'checkbox', label: '', autoWidth: true },
              { id: 'name', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.nameLabel` }) },
              { id: 'createdDate', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.createdDateLabel` }) },
              { id: 'updatedDate', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.updatedDateLabel` }) },
              { id: 'active', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.activeLabel` }), align: 'center' },
              { id: 'actions', label: '', align: 'center', autoWidth: true },
            ]}
            orderBy="name"
            rows={feeds}
            sort={setFeeds}
            isLoading={isFetching}
            noResult={{
              show: renderRows.length <= 0,
              text: 'No Results',
            }}>
            {renderRows.slice(from, to).map((row, idx) => (
              <ListRow key={idx} onClick={() => handleOnClick(`/feed-management/manual-feeds/${row.id}`)}>
                <ListCell align="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
                  <Checkbox
                    disableRipple
                    color="primary"
                    tabIndex={-1}
                    checked={selectedFeeds.includes(row.id)}
                    icon={<Square size={22} color={theme.palette.darkBlue.main} />}
                    checkedIcon={<SquareCheckBig size={22} color={theme.palette.darkBlue.main} />}
                    onChange={(_, checked) => handleSelectFeeds(row.id, checked)}
                  />
                </ListCell>
                <ListCell>{row.name}</ListCell>
                <ListCell>
                  <FormattedDate value={row.createdDate} day="2-digit" month="short" year="numeric" />
                </ListCell>
                <ListCell>
                  <FormattedDate value={row.updatedDate} day="2-digit" month="short" year="numeric" />
                </ListCell>
                <ListCell align={isMobile ? 'right' : 'center'}>
                  <Box>{row.active && <CheckCircle color={theme.palette.darkBlue.main} />}</Box>
                </ListCell>
                <ListCell align="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
                  <Box display={'flex'} justifyContent={'space-between'}>
                    <Button size={'small'} onClick={() => handleOnClick(`/feed-management/manual-feeds/${row.id}`)}>
                      <FormattedMessage id="general.edit" values={{ type: '' }} />
                    </Button>
                  </Box>
                </ListCell>
              </ListRow>
            ))}
          </List>
          <Pagination currentPage={currentPage} pages={Math.ceil(renderRows.length / PAGINATION_LIMIT)} onClick={handlePagination} />
        </Grid>
      </Paper>
    </>
  );
};

export default ListManualFeeds;
